import DOMPurify from 'dompurify';

/**
 * Sanitize HTML content to prevent XSS attacks
 * @param {string} html - The HTML string to sanitize
 * @returns {string} - Sanitized HTML string
 */
export const sanitizeHTML = (html) => {
  if (!html) return '';
  return DOMPurify.sanitize(html, {
    USE_PROFILES: { html: true },
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li', 'a', 'img', 'blockquote', 'pre', 'code',
      'table', 'thead', 'tbody', 'tr', 'th', 'td',
      'div', 'span', 'hr'
    ],
    ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'class', 'target', 'rel'],
    ADD_ATTR: ['target']  // Ensure target="_blank" is preserved
  });
};

/**
 * Create sanitized HTML props for dangerouslySetInnerHTML
 * @param {string} html - The HTML string to sanitize
 * @returns {object} - Object with __html property
 */
export const createSanitizedMarkup = (html) => {
  return { __html: sanitizeHTML(html) };
};

/**
 * Parse markdown-style links and convert to HTML
 * Supports: [link text](/internal-path) and [link text](https://external-url)
 * @param {string} text - The text containing markdown links
 * @returns {string} - HTML string with links converted
 */
export const parseMarkdownLinks = (text) => {
  if (!text) return '';
  
  // Regex to match markdown links: [text](url)
  const markdownLinkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
  
  const html = text.replace(markdownLinkRegex, (match, linkText, url) => {
    // Determine if external link
    const isExternal = url.startsWith('http://') || url.startsWith('https://');
    
    if (isExternal) {
      return `<a href="${url}" target="_blank" rel="noopener noreferrer">${linkText}</a>`;
    } else {
      return `<a href="${url}">${linkText}</a>`;
    }
  });
  
  // Sanitize the result to prevent XSS
  return sanitizeHTML(html);
};
